import { useCallback, useEffect, useRef, useState } from 'react';

import { useSpeedBalance } from '@/features/wallet';

/** Post-deposit/withdraw re-poll cadence while the backend saga settles. */
const SETTLEMENT_POLL_DELAYS = [3_000, 6_000, 10_000, 15_000];
const SETTLEMENT_SAFETY_TIMEOUT = 16_000;

interface CustodialBalanceResult {
    /**
     * Tradeable USDC balance = custodial + adapter + HyperCore-withdrawable,
     * as a numeric value. `null` while unavailable.
     */
    numericBalance: number | null;
    /** USDC the withdraw saga can actually move, as the server reports it; never derived here. */
    numericWithdrawable: number | null;
    /** USDC held by the custodial trading wallet itself on HyperEVM; null while unavailable. */
    custodialUsdc: number | null;
    /** USDC held by the per-user adapter on HyperEVM; null while unavailable. */
    adapterUsdc: number | null;
    /** USDC the adapter can withdraw from HyperCore; null while unavailable. */
    hyperCoreWithdrawable: number | null;
    /** Floor (USD) for the next withdrawal; null on older backends. */
    minWithdrawUsd: number | null;
    /** Estimated flat cost (USD) of the next withdrawal; null on older backends. */
    flatFeeUsd: number | null;
    /** Whether the initial balance read is in progress. */
    isLoading: boolean;
    /** Whether the balance read has errored. */
    isError: boolean;
    /** Backend error code when the balance is unavailable. */
    errorCode: string | null;
    /** User-readable backend error message when the balance is unavailable. */
    errorMessage: string | null;
    /** True when the user needs a custodial wallet before speed trading can continue. */
    isCustodialWalletMissing: boolean;
    /** Whether a post-deposit/withdraw re-poll is in flight. */
    isSettling: boolean;
    /** Trigger a single immediate refetch of the speed balance. */
    refetch: () => void;
    /** Re-poll the speed balance a few times while the saga settles. */
    refetchUntilSettled: () => void;
}

/**
 * Custodial ("Quick Trade") wallet balance for the NEW speed-trading backend.
 *
 * Wraps `useSpeedBalance()` — the single source of truth for tradeable USDC
 * spread across the custodial wallet, per-user adapter, and HyperCore margin.
 * Takes no parameters; balance is always for the authenticated user.
 *
 * `refetchUntilSettled()` triggers a short burst of refetches (used after a
 * deposit/withdraw completes) and exposes `isSettling` for UI loading hints.
 */
export function useCustodialBalance(): CustodialBalanceResult {
    const {
        numericTotal,
        numericWithdrawable,
        custodialUsdc,
        adapterUsdc,
        hyperCoreWithdrawable,
        minWithdrawUsd,
        flatFeeUsd,
        isLoading,
        isError,
        errorCode,
        errorMessage,
        isCustodialWalletMissing,
        refetch,
    } = useSpeedBalance();

    const [isSettling, setIsSettling] = useState(false);
    const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    const clearTimers = useCallback(() => {
        timersRef.current.forEach(clearTimeout);
        timersRef.current = [];
    }, []);

    const refetchUntilSettled = useCallback(() => {
        clearTimers();
        setIsSettling(true);
        refetch();

        const timers = SETTLEMENT_POLL_DELAYS.map((ms) =>
            setTimeout(() => refetch(), ms),
        );
        timers.push(
            setTimeout(() => setIsSettling(false), SETTLEMENT_SAFETY_TIMEOUT),
        );
        timersRef.current = timers;
    }, [clearTimers, refetch]);

    useEffect(() => clearTimers, [clearTimers]);

    return {
        numericBalance: numericTotal,
        numericWithdrawable,
        custodialUsdc,
        adapterUsdc,
        hyperCoreWithdrawable,
        minWithdrawUsd,
        flatFeeUsd,
        isLoading,
        isError,
        errorCode,
        errorMessage,
        isCustodialWalletMissing,
        isSettling,
        refetch,
        refetchUntilSettled,
    };
}
